<script setup>
import { slugifyString } from '~/utils/slugify'

const props = defineProps({
  image: {
    type: Object,
  },
  overlay: {
    type: Object,
  },
  content: {
    type: Object,
  },
  slider: {
    type: Object,
  },
})
</script>

<template>
  <div class="row">
    <div class="col-12">
      <div class="itss">
        <div class="d-flex flex-column flex-880-row-reverse">
          <div class="itss__image-container">
            <NuxtImg
              sizes="sm:340px md:440px lg:860px"
              densities="1x 2x"
              :src="props.image.src"
              :alt="props.image.alt"
              class="img-fluid itss__image"
              loading="lazy"
              format="webp"
              :placeholder="[300, 200, 60, 2]"
            />
            <div
              v-if="props.overlay"
              class="itss__overlay"
            >
              <img
                :src="props.overlay.src"
                :alt="props.overlay.alt"
                class="img-fluid"
                loading="lazy"
              >
            </div>
          </div>
          <div class="itss__content">
            <h2
              v-if="props.content.title"
              :id="slugifyString(props.content.title)"
              class="scale-3 scale-3--bold-uppercase itss__title"
            >
              {{ props.content.title }}
            </h2>
            <div class="scale-1 scale-1--serif itss__text">
              {{ props.content.text }}
            </div>
            <FormButton
              v-if="props.content?.button?.label"
              class="l-button--outlined image-text__button"
              :label="props.content.button.label"
              :to="props.content.button.to"
              :rel="props.content.button.rel"
              component="NuxtLink"
            />
            <div class="itss__slider">
              <Slider
                class="slider--fade-out slider--overflow slider--picker"
                :show-pagination="true"
                :wrap-around="true"
                :slides="props.slider"
                align="center"
              >
                <template #default="slide">
                  <ProductTeaser
                    :slide="slide"
                    parent-type="category-teaser"
                    :parent-title="props.content.title"
                    :index="slide.index"
                  />
                </template>
              </Slider>
            </div>
          </div>
        </div>
        <hr class="d-block d-880-none">
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/scss/variables.scss';
@import '@/node_modules/bootstrap/scss/mixins';

// image text slider side
.itss {
  position: relative;

  border: 1px solid $color-dark-01;
  border-radius: 12px;
  overflow: hidden;

  @include media-breakpoint-down(880) {
    border: unset;
    border-radius: unset;
    overflow: unset;
  }

  &__image-container {
    position: relative;
    display: flex;
    width: 50%;
    height: auto;
    border-left: 1px solid $color-dark-01;

    @include media-breakpoint-down(880) {
      width: unset;
      border-left: unset;

      &::after {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(rgba(0, 0, 0, 0), rgba(0, 0, 0, 1));
      }
    }

    @include media-breakpoint-down(880) {
      margin: 0 $padding-container-negative;
    }
  }

  &__image {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 360px;
    display: flex;
    object-fit: cover;

    :deep(.image-product) {
      max-height: 267px;
    }

    @include media-breakpoint-down(880) {
      max-height: calc(208px + 150px);
    }
  }

  &__overlay {
    display: flex;
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    z-index: 10;

    @include media-breakpoint-down(880) {
      max-height: 208px;
    }

    img {
      display: flex;
      margin: auto;
      max-height: 402px;
      max-width: 480px;
      width: 100%;
      height: 100%;
      padding: 50px;

      @include media-breakpoint-down(880) {
        max-height: 128px;
        max-width: 204px;
        padding: 20px;
      }
    }
  }

  &__content {
    overflow: hidden;
    padding: 50px 50px 20px 50px;
    width: 50%;

    @include media-breakpoint-down(880) {
      overflow: unset;
      width: unset;
      padding: 0;
      margin-top: -150px;
      z-index: 10;
    }
  }

  &__title {
    margin-bottom: 5px;
  }

  &__text {
    margin-bottom: 20px;
  }

  &__slider {
    margin-top: 50px;

    @include media-breakpoint-down(880) {
      margin-top: 30px;
    }

    @include media-breakpoint-up(880) {
      :deep(.slider--overflow) {
        .slider__carousel {
          all: unset !important;
        }
      }
    }

    :deep(.embla__slide) {
      flex: 0 0 83.333%; /* Default slide covers 100% of the viewport */
    }
    @media (min-width: 560px) {
      :deep(.embla__slide) {
        flex: 0 0 62.5%; /* Breakpoint SM slide covers 50% of the viewport */
      }
    }
    @media (min-width: 720px) {
      :deep(.embla__slide) {
        flex: 0 0 45.4545%; /* Breakpoint SM slide covers 50% of the viewport */
      }
    }
    @media (min-width: 880px) {
      :deep(.embla__slide) {
        flex: 0 0 62.25%; /* Breakpoint SM slide covers 50% of the viewport */
      }
    }
    @media (min-width: 1520px) {
      :deep(.embla__slide) {
        flex: 0 0 45.4545%; /* Breakpoint SM slide covers 50% of the viewport */
      }
    }
  }

  hr {
    color: $color-dark-01;
    opacity: 1;
    margin: 20px $padding-container-negative 0 $padding-container-negative;
  }
}
</style>
